import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import SEOHead from '@/components/feature/SEOHead';
import { categories, movies, articles } from '@/mocks/movies';

const featuredMovies = movies.filter((m) => m.featured);
const hotMovies = movies.filter((m) => m.hot);
const allMovies = movies;

export default function Home() {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const filteredMovies = activeCategory
    ? allMovies.filter((m) => m.categorySlug === activeCategory)
    : allMovies;

  return (
    <>
      <SEOHead
        title="星辰影院：高清电影 - 星辰影院电影大全 | 最新蓝光4K电影免费在线观看不缓冲"
        description="星辰影院提供海量高清电影资源，涵盖电影大全、最新电影、蓝光4K画质，免费在线观看不卡顿不缓冲，一站式满足你的观影需求。"
        keywords="星辰影院,高清电影,电影大全,最新电影,蓝光电影,4K电影,免费在线观看,不缓冲"
        canonicalUrl="https://www.xingchencinema.com/"
      />

      <Navbar />

      <main>
        <section
          className="relative w-full h-[420px] md:h-[600px] overflow-hidden"
          aria-label="首页英雄区域"
        >
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20movie%20theater%20interior%20with%20dramatic%20red%20velvet%20curtains%20and%20golden%20ambient%20lighting%2C%20rows%20of%20empty%20premium%20seats%20facing%20a%20massive%20glowing%20screen%2C%20art%20deco%20architectural%20details%2C%20warm%20cinematic%20atmosphere%2C%20deep%20red%20and%20gold%20tones%2C%20elegant%20and%20luxurious%20movie%20palace%20aesthetic%2C%20soft%20bokeh%20light%20effects%2C%20highly%20detailed%2C%208K%20quality%2C%20no%20visible%20text%20or%20logos&width=1800&height=800&seq=hero-bg-2024&orientation=landscape"
            alt="星辰影院 - 高清电影免费在线观看"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <div className="animate-fade-in-up w-full">
              <h2 className="text-white font-heading text-2xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 leading-tight">
                海量高清电影
                <br />
                <span className="text-primary-400">免费在线观看</span>
              </h2>
              <p className="text-white/80 text-sm md:text-base max-w-2xl mx-auto mb-6 md:mb-8 leading-relaxed">
                蓝光4K画质 · 极速播放不缓冲 · 最新电影每日更新 · 一站式观影体验
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/kehuan"
                  className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-full font-medium text-sm transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-play-circle-line"></i>
                  </span>
                  立即观影
                </Link>
                <a
                  href="#categories"
                  className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white border border-white/30 px-6 py-3 rounded-full font-medium text-sm transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-movie-line"></i>
                  </span>
                  浏览分类
                </a>
              </div>
            </div>
          </div>
        </section>

        <section
          id="categories"
          className="py-10 md:py-14 bg-background-50"
          aria-label="电影分类导航"
        >
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-3">
                探索电影世界
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                覆盖动作、科幻、喜剧、爱情等十大分类，满足你的所有观影喜好
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-background-200 bg-background-50 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-500 transition-all text-sm text-foreground-700 font-medium whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-sm`}></i>
                  </span>
                  {cat.name}
                  <span className="text-xs text-foreground-400 ml-1">
                    {cat.count}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section
          className="py-10 md:py-14 bg-background-100"
          aria-label="精选推荐电影"
        >
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-2">
                  精选推荐
                </h2>
                <p className="text-sm text-foreground-600">编辑精选 · 口碑佳作</p>
              </div>
              <Link
                to="/category/kehuan"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1 cursor-pointer"
              >
                查看全部
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {featuredMovies.slice(0, 4).map((movie) => (
                <MovieCard
                  key={movie.id}
                  id={movie.id}
                  title={movie.title}
                  titleEn={movie.titleEn}
                  category={movie.category}
                  categorySlug={movie.categorySlug}
                  year={movie.year}
                  rating={movie.rating}
                  quality={movie.quality}
                  duration={movie.duration}
                  imageUrl={movie.imageLandscape}
                  featured
                />
              ))}
            </div>
          </div>
        </section>

        <section
          id="films"
          className="py-10 md:py-14 bg-background-50"
          aria-label="全部电影"
        >
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-2">
                  电影大全
                </h2>
                <p className="text-sm text-foreground-600">海量高清资源 · 随心选择</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-6 pb-1">
              <button
                onClick={() => setActiveCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === null
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 border border-background-200'
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 border border-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {filteredMovies.map((movie) => (
                <MovieCard
                  key={movie.id}
                  id={movie.id}
                  title={movie.title}
                  titleEn={movie.titleEn}
                  category={movie.category}
                  categorySlug={movie.categorySlug}
                  year={movie.year}
                  rating={movie.rating}
                  quality={movie.quality}
                  duration={movie.duration}
                  imageUrl={movie.imagePortrait}
                />
              ))}
            </div>
          </div>
        </section>

        {hotMovies.length > 0 && (
          <section
            className="py-10 md:py-14 bg-background-100"
            aria-label="热门电影"
          >
            <div className="max-w-[1440px] mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-2">
                    热门推荐
                  </h2>
                  <p className="text-sm text-foreground-600">大家都在看 · 好评如潮</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {hotMovies.map((movie) => (
                  <MovieCard
                    key={movie.id}
                    id={movie.id}
                    title={movie.title}
                    titleEn={movie.titleEn}
                    category={movie.category}
                    categorySlug={movie.categorySlug}
                    year={movie.year}
                    rating={movie.rating}
                    quality={movie.quality}
                    duration={movie.duration}
                    imageUrl={movie.imagePortrait}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        <section
          className="py-10 md:py-14 bg-background-50"
          aria-label="最新资讯"
        >
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-2">
                  电影资讯
                </h2>
                <p className="text-sm text-foreground-600">最新影评 · 观影指南 · 行业动态</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.slice(0, 3).map((article) => (
                <article key={article.id} className="group cursor-pointer">
                  <Link to={`/article/${article.slug}`} className="block">
                    <div className="aspect-[16/9] rounded-lg overflow-hidden mb-4">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex items-center gap-3 mb-2 text-xs text-foreground-500">
                      <span className="bg-accent-100 text-accent-900 px-2 py-0.5 rounded-md font-medium">
                        {article.category}
                      </span>
                      <span>{article.date}</span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-eye-line text-[10px]"></i>
                        </span>
                        {article.views.toLocaleString()}
                      </span>
                    </div>
                    <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          className="py-10 md:py-14 bg-background-100"
          aria-label="常见问题"
        >
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                关于星辰影院的常见疑问，这里都有解答
              </p>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {[
                {
                  q: '星辰影院的电影资源是否需要付费？',
                  a: '星辰影院所有电影资源均为免费在线观看，无需注册、无需付费。我们致力于为广大影迷提供便捷、高清的观影服务。',
                },
                {
                  q: '星辰影院的视频画质如何？',
                  a: '我们提供从720P到蓝光4K多种画质选择，大部分热门影片支持蓝光4K超高清画质，确保您享受到极致的视觉体验。',
                },
                {
                  q: '观看电影会卡顿或缓冲吗？',
                  a: '星辰影院采用先进的CDN加速技术，在全国范围内部署了高速服务器节点，确保播放流畅不卡顿。建议使用稳定的网络连接以获得最佳观影体验。',
                },
                {
                  q: '网站的电影资源多久更新一次？',
                  a: '我们的内容团队每日更新电影资源，最新院线下映大片会在第一时间上线，敬请关注。',
                },
                {
                  q: '可以在手机上观看吗？',
                  a: '当然可以！星辰影院完美适配手机、平板、电脑等各类设备，随时随地畅享高清电影。无需下载APP，打开浏览器即可观看。',
                },
              ].map((faq, idx) => (
                <details
                  key={idx}
                  className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer"
                >
                  <summary className="flex items-center justify-between px-5 py-4 font-medium text-foreground-800 text-sm cursor-pointer list-none">
                    <span>{faq.q}</span>
                    <span className="w-5 h-5 flex items-center justify-center transition-transform group-open:rotate-180">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}